import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getGigs } from '../../services/gig.service';
import { getUserOrders } from '../../services/order.service';

const ExploreGigsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // 1. Fetch user orders to identify interests
  const { data: ordersData } = useQuery({
    queryKey: ['orders', 'interests'],
    queryFn: () => getUserOrders({ type: 'purchases' }),
    staleTime: 60 * 1000,
  });

  const categoryInterests = useMemo(() => {
    const orders = ordersData?.orders || [];
    const categories = new Set();
    orders.forEach(order => {
      if (order.gigId?.category) {
        if (Array.isArray(order.gigId.category)) {
          order.gigId.category.forEach(cat => categories.add(typeof cat === 'object' ? cat._id : cat));
        } else {
          categories.add(typeof order.gigId.category === 'object' ? order.gigId.category._id : order.gigId.category);
        }
      }
    });
    return Array.from(categories).join(',');
  }, [ordersData]);

  // 2. Fetch gigs based on interests or popular ones
  const { data: gigsData, isLoading: gigsLoading } = useQuery({
    queryKey: ['gigs', 'explore', categoryInterests],
    queryFn: () => getGigs({ 
      category: categoryInterests || undefined, 
      sort: 'sales', 
      limit: 6 
    }),
    staleTime: 60 * 1000,
  });

  const gigs = gigsData?.gigs || [];
  const itemsPerPage = 2;
  const totalPages = Math.ceil(gigs.length / itemsPerPage);

  const nextSlide = () => {
    if (isAnimating || totalPages <= 1) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % totalPages);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const prevSlide = () => {
    if (isAnimating || totalPages <= 1) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
    setTimeout(() => setIsAnimating(false), 300);
  };

  if (gigsLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto p-6 animate-pulse">
        <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
        <div className="flex gap-6">
          <div className="flex-1 h-64 bg-gray-100 dark:bg-[#1d2026] rounded-[10px]"></div>
          <div className="flex-1 h-64 bg-gray-100 dark:bg-[#1d2026] rounded-[10px]"></div>
        </div>
      </div>
    );
  }

  if (gigs.length === 0) return null;

  return (
    <div className="w-full max-w-4xl mx-auto p-6 font-['Poppins']">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-medium md:font-semibold text-gray-900 dark:text-white tracking-tight">
          Explore Gigs
        </h2>
        
        {/* Navigation Controls */}
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={prevSlide}
              disabled={isAnimating}
              className="p-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <button
              onClick={nextSlide}
              disabled={isAnimating}
              className="p-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        )}
      </div>

      {/* Carousel Container */}
      <div className="relative overflow-hidden">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {Array.from({ length: totalPages }).map((_, pageIndex) => (
            <div key={pageIndex} className="flex gap-6 min-w-full">
              {gigs
                .slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage)
                .map((gig) => {
                  const lowestPrice = gig.pricing?.basic?.price || 0;
                  const creatorName = gig.creator ? `${gig.creator.firstName} ${gig.creator.lastName}` : 'Pro Designer';
                  const hellociansCount = gig.hellocians?.length || 0;

                  return (
                    <div
                      key={gig._id}
                      onClick={() => window.location.href = `/gigs/${gig.slug || gig._id}`}
                      className="flex-1 bg-white dark:bg-[#1d2026] rounded-[10px] overflow-hidden transition-all duration-200 cursor-pointer group shadow-sm border border-gray-100 dark:border-gray-800"
                    >
                      {/* Gig Image */}
                      <div className="relative rounded-t-[10px] overflow-hidden aspect-[16/10]">
                        <img
                          src={gig.cover}
                          alt={gig.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3 bg-white/90 dark:bg-[#1d2026]/90 backdrop-blur-sm px-2 py-1 rounded text-[11px] font-bold text-[#174568] dark:text-gray-200">
                          From ${lowestPrice}
                        </div>
                      </div>

                      {/* Gig Content */}
                      <div className="p-4">
                        <p className="text-[8px] text-blue-600 dark:text-blue-400 font-medium uppercase tracking-wider mb-1">
                          {gig.category?.[0]?.name || 'Service'}
                        </p>
                        <h3 className="font-medium text-gray-900 dark:text-white text-[12px] mb-3 group-hover:text-[#174568] dark:group-hover:text-blue-400 transition-colors duration-200 line-clamp-2 min-h-[40px]">
                          {gig.title}
                        </h3>

                        <div className="flex items-center justify-between pt-3 border-t border-gray-50 dark:border-gray-800/50">
                          <div className="flex items-center gap-2 overflow-hidden">
                            <span className="text-[10px] text-gray-600 dark:text-gray-400 truncate font-normal">
                              {creatorName}
                            </span>
                          </div>

                          <div className="flex items-center -space-x-2 shrink-0">
                            {/* Creator Avatar */}
                            <img 
                              src={gig.creator?.profilePicture || 'https://placehold.co/600x400'} 
                              className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-800 object-cover shadow-sm bg-white dark:bg-[#1d2026]"
                              alt={creatorName}
                              title={`Creator: ${creatorName}`}
                            />
                            {/* Hellocians Avatars */}
                            {gig.hellocians?.slice(0, 2).map((h, i) => (
                              <img
                                key={h._id || i}
                                src={h.profilePicture || 'https://placehold.co/600x400'}
                                className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-800 object-cover shadow-sm bg-white dark:bg-[#1d2026]"
                                alt="Hellocian"
                                style={{ zIndex: 10 - i }}
                              />
                            ))}
                            {hellociansCount > 2 && (
                              <div 
                                className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-800 bg-gray-50 dark:bg-gray-700 flex items-center justify-center shadow-sm z-0 text-[9px] font-bold text-gray-500 dark:text-gray-300"
                              >
                                +{hellociansCount - 2}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExploreGigsCarousel;